import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  AddManagerIncomingWebhookBody,
  ManagerIncomingWebhookAttributes,
  UpdateManagerIncomingWebhookBody,
} from '@loyalty/shared-common/manager-api-client';
import {
  IncomingWebhookAuthenticationType,
  IncomingWebhookType,
} from '@loyalty/shared-common/ingestor-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { uuid } from '@loyalty/shared-common';
import { DEFAULT_MAPPED_EVENT_CONFIG } from './components/detail/mapped-events/IncomingWebhookMappedEventConfig';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { extractCountersAndTags } from './incoming-webhook-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';

export interface IIncomingWebhooksContext {
  webhooks: ManagerIncomingWebhookAttributes[];
  selectedWebhook: ManagerIncomingWebhookAttributes | undefined;
  error: any;
  createWebhook: () => void;
  addWebhook: (webhook: AddManagerIncomingWebhookBody) => Promise<void>;
  updateWebhook: (webhook: UpdateManagerIncomingWebhookBody) => Promise<void>;
  deleteWebhook: (webhookId: string) => void;
  setSelectedWebhook: (incomingWebhook: ManagerIncomingWebhookAttributes | undefined) => void;
  getWebhooks: () => Promise<void>;
  isLoading: IsLoading;
}

export const IncomingWebhooksContext = createContext<IIncomingWebhooksContext>({} as any);

export function useIncomingWebhooks() {
  return useContext(IncomingWebhooksContext);
}

export function IncomingWebhooksProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { managerApi } = useApi();
  const { currentAccount } = useAccounts();
  const [error, setError] = useState<any>();
  const [webhooks, setWebhooks] = useState<ManagerIncomingWebhookAttributes[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<
    ManagerIncomingWebhookAttributes | undefined
  >();

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const createWebhook = useCallback(() => {
    const webhook: ManagerIncomingWebhookAttributes = {
      accountId: currentAccount.id,
      programId: program.id,
      id: uuid(),
      name: '',
      type: IncomingWebhookType.MAPPED_EVENT,
      authenticationConfig: {
        type: IncomingWebhookAuthenticationType.HUB_SIGNATURE,
        secret: '',
        verificationSecret: '',
      },
      url: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      config: DEFAULT_MAPPED_EVENT_CONFIG,
      isNew: true,
    };

    setWebhooks(prev => [webhook, ...prev]);
    setSelectedWebhook(webhook);
  }, [currentAccount, program]);

  const addWebhook = useCallback(
    async (webhook: AddManagerIncomingWebhookBody) => {
      addLoader('addWebhook');
      try {
        const newWebhook = await managerApi.addIncomingWebhook(program.id, webhook);
        const { tags, counters } = await extractCountersAndTags(newWebhook);
        if (newWebhook.config) {
          newWebhook.config.counters = counters;
          newWebhook.config.tags = tags;
        }
        setWebhooks(prev => prev.map(hook => (hook.id === webhook.id ? newWebhook : hook)));
        setSelectedWebhook(newWebhook);
        notify(t('shared.saved'), 'success');
      } catch (error) {
        setError(error);
        notify(t('incoming-webhooks.errors.save', { error }), 'error');
      } finally {
        removeLoader('addWebhook');
      }
    },
    [addLoader, removeLoader, managerApi, program, notify, t],
  );

  const updateWebhook = useCallback(
    async (webhook: UpdateManagerIncomingWebhookBody) => {
      addLoader('updateWebhook');
      try {
        const updatedWebhook = await managerApi.updateIncomingWebhook(webhook);
        const { counters, tags } = await extractCountersAndTags(updatedWebhook);
        if (updatedWebhook.config) {
          updatedWebhook.config.counters = counters;
          updatedWebhook.config.tags = tags;
        }
        setWebhooks(prev => prev.map(hook => (hook.id === webhook.id ? updatedWebhook : hook)));
        setSelectedWebhook(updatedWebhook);
        notify(t('shared.saved'), 'success');
      } catch (error) {
        setError(error);
        notify(t('incoming-webhooks.errors.save', { error }), 'error');
      } finally {
        removeLoader('updateWebhook');
      }
    },
    [addLoader, removeLoader, managerApi, notify, t],
  );

  const deleteWebhook = useCallback(
    async (webhookId: string) => {
      addLoader('deleteWebhook');
      try {
        const webhook = webhooks.find(({ id }) => id === webhookId);
        if (!webhook) {
          throw new Error('Can not find incoming webhook');
        }
        if (webhook.isNew) {
          setWebhooks(prev => {
            return prev.filter(webhook => webhook.id !== webhookId);
          });
          removeLoader('deleteWebhook');
        } else {
          await managerApi.deleteIncomingWebhook(webhookId);
          setWebhooks(prev => {
            return prev.filter(webhook => webhook.id !== webhookId);
          });
        }
      } catch (error) {
        notify(t('incoming-webhooks.errors.delete', { error }), 'error');
      } finally {
        removeLoader('deleteWebhook');
      }
    },
    [addLoader, removeLoader, managerApi, notify, t, webhooks],
  );

  const getWebhooks = useCallback(async () => {
    addLoader('getWebhooks');
    try {
      const webhooks = await managerApi.getProgramIncomingWebhooks(program.id);
      for (const webhook of webhooks) {
        const { counters, tags } = await extractCountersAndTags(webhook);
        if (webhook.config) {
          webhook.config.counters = counters;
          webhook.config.tags = tags;
        }
      }
      setWebhooks(webhooks);
    } catch (error) {
      setError(error);
    } finally {
      removeLoader('getWebhooks');
    }
  }, [addLoader, removeLoader, managerApi, program]);

  useEffect(() => {
    getWebhooks();
  }, [getWebhooks]);

  const context = {
    webhooks,
    selectedWebhook,
    error,
    createWebhook,
    addWebhook,
    updateWebhook,
    deleteWebhook,
    setSelectedWebhook,
    getWebhooks,
    isLoading,
  };

  return (
    <IncomingWebhooksContext.Provider value={context}>{children}</IncomingWebhooksContext.Provider>
  );
}
